import { syncStore } from '../stores/sync-store.js';

import { isConflictPayload } from './conflict-types.js';
import { getOfflineDb, type QueueItem } from './db.js';
import {
  computeBackoff,
  countConflicts,
  countPending,
  markConflict,
  markFailed,
  markInFlight,
  markPermanentlyFailed,
  markSucceeded,
} from './queue.js';

/**
 * Phase 10 — the foreground sync engine. We deliberately don't use the
 * Background Sync / Periodic Sync APIs (spec out of scope), so the
 * engine wakes up whenever the network flips or whenever the user
 * touches the tab.
 *
 * Strategy:
 *   1. On `online` (or every 30 s as a safety net), scan for items in
 *      `pending` whose `nextAttemptAt` is in the past.
 *   2. For each item: upload its photos (if any), then POST/PATCH the
 *      mutation with `Idempotency-Key` so the server can dedup.
 *   3. On `2xx` → delete the queue item; on `409 conflict` → freeze it
 *      and surface in `/sync/conflicts`; on anything else → exponential
 *      backoff (1 s, 4 s, 16 s, 60 s, plateau).
 */

interface SyncEngineDeps {
  /**
   * Sends one queued mutation. Must throw with `(err as ApiError).status`
   * and `(err as ApiError).details` when the server returns a structured
   * conflict envelope.
   */
  sendMutation: (item: QueueItem) => Promise<void>;
  /** Hook used in tests to skip the real photo upload path. */
  uploadPhotos?: (item: QueueItem) => Promise<void>;
  /** Logger override. */
  log?: (msg: string, meta?: Record<string, unknown>) => void;
}

let _running = false;
let _interval: number | null = null;
let _userId: string | null = null;
let _deps: SyncEngineDeps | null = null;

const POLL_MS = 30_000;
const MAX_ATTEMPTS = 12;

async function refreshStoreCounters(userId: string): Promise<void> {
  const [pending, conflicts] = await Promise.all([
    countPending(userId),
    countConflicts(userId),
  ]);
  syncStore.getState().setPendingCount(pending);
  syncStore.getState().setConflictCount(conflicts);
}

async function processOne(item: QueueItem, deps: SyncEngineDeps): Promise<void> {
  await markInFlight(item.id);
  try {
    if (deps.uploadPhotos) await deps.uploadPhotos(item);
    await deps.sendMutation(item);
    await markSucceeded(item.id);
    deps.log?.('sync.success', { id: item.id, endpoint: item.endpoint });
  } catch (err: unknown) {
    const status = (err as { status?: number }).status;
    const details = (err as { details?: unknown }).details;
    if (status === 409 && isConflictPayload(details)) {
      await markConflict(item.id, details);
      deps.log?.('sync.conflict', { id: item.id, kind: details.kind });
      return;
    }
    if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
      // Hard client error → don't keep retrying.
      const msg = (err as Error).message ?? `HTTP ${status}`;
      await markPermanentlyFailed(item.id, msg);
      deps.log?.('sync.failed_permanent', { id: item.id, status, msg });
      return;
    }
    const attempt = item.attemptCount + 1;
    if (attempt >= MAX_ATTEMPTS) {
      await markPermanentlyFailed(
        item.id,
        (err as Error).message ?? 'Max retries exceeded',
      );
      deps.log?.('sync.failed_max_retries', { id: item.id });
      return;
    }
    const delay = computeBackoff(attempt);
    const msg = (err as Error).message ?? `HTTP ${status ?? '???'}`;
    await markFailed(item.id, msg, delay);
    deps.log?.('sync.backoff', { id: item.id, attempt, delay });
  }
}

export async function flushNow(): Promise<void> {
  if (!_userId || !_deps) return;
  const db = getOfflineDb();
  const due = await db
    .queueItems.where('[userId+status]')
    .equals([_userId, 'pending'])
    .filter((row) => row.nextAttemptAt <= Date.now())
    .sortBy('createdAt');

  if (due.length === 0) {
    await refreshStoreCounters(_userId);
    return;
  }

  syncStore.getState().setPendingCount(due.length);
  syncStore.getState().recomputeState();

  // Process sequentially within each groupKey, parallel across groups.
  const byGroup = new Map<string | null, QueueItem[]>();
  for (const item of due) {
    const key = item.groupKey ?? null;
    const bucket = byGroup.get(key) ?? [];
    bucket.push(item);
    byGroup.set(key, bucket);
  }

  await Promise.all(
    Array.from(byGroup.values()).map(async (group) => {
      for (const item of group) {
        await processOne(item, _deps!);
      }
    }),
  );

  await refreshStoreCounters(_userId);
}

export function startSyncEngine(userId: string, deps: SyncEngineDeps): void {
  if (_running) return;
  _running = true;
  _userId = userId;
  _deps = deps;

  void refreshStoreCounters(userId);

  const tick = () => {
    if (!syncStore.getState().isOnline) return;
    void flushNow();
  };

  const onOnline = () => tick();
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
  }
  _interval = (typeof window !== 'undefined'
    ? window.setInterval(tick, POLL_MS)
    : null) as unknown as number | null;
}

export function stopSyncEngine(): void {
  _running = false;
  _userId = null;
  _deps = null;
  if (_interval !== null && typeof window !== 'undefined') {
    window.clearInterval(_interval);
    _interval = null;
  }
}

/** Test-only — exposed so unit tests can drive the engine deterministically. */
export const __internal = {
  processOne,
  refreshStoreCounters,
};
